/**
 * Mount Chat Panel
 *
 * Bridges the React ChatPanel into the vanilla DOM world of index.ts.
 * Creates a fixed-position container, renders the ChatPanel via React,
 * and exposes toggle/dispose controls.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ChatPanel } from './ChatPanel';
import { agentService } from './AgentService';
import { saveSessionToScene } from '../editor-content';
import type { TraceScene } from 'trace-viewer/scene';
import type { SceneObject } from 'trace-viewer/scene';
import type { TraceViewerElement } from 'trace-viewer';
import type { SceneFileImporter } from './scene-files';

export interface ChatPanelControls {
    toggle: () => void;
    isVisible: () => boolean;
    dispose: () => void;
}

export function mountChatPanel(
    _viewerWrapper: HTMLDivElement,
    getTraceScene: () => TraceScene | null,
    getSelectedObject: () => SceneObject | null,
    getViewer: () => TraceViewerElement | null,
    /** The canvas drop's import, for files the user hands the assistant. */
    importFiles: SceneFileImporter,
): ChatPanelControls {
    agentService.setSceneFileImporter(importFiles);
    // Create container fixed to viewport (overlays everything including inspector)
    const container = document.createElement('div');
    container.id = 'ai-chat-container';
    document.body.appendChild(container);

    let root: Root | null = null;
    let visible = false;

    function render() {
        root ??= createRoot(container);

        // Update agent service context on each render
        agentService.setScene(getTraceScene());
        agentService.setViewer(getViewer());
        agentService.setSelectedObject(getSelectedObject());

        root.render(
            React.createElement(ChatPanel, {
                visible,
                onClose: () => {
                    visible = false;
                    render();
                },
            })
        );
    }

    // Listen for selection changes to keep agent context in sync
    const onSelectionChanged = () => {
        agentService.setSelectedObject(getSelectedObject());
    };
    window.addEventListener('trace-viewer:selection-changed', onSelectionChanged);

    // Listen for scene changes
    const onSceneChanged = () => {
        agentService.setScene(getTraceScene());
        agentService.setViewer(getViewer());
    };
    window.addEventListener('trace-viewer:scene-changed', onSceneChanged);

    // Each finished turn is written into the scene's editor content, so a
    // save or download carries the conversation (CONTENT_MODEL_SPEC §2.10) —
    // into the scene it was recorded in, never one opened while it ran.
    const unsubSession = agentService.session.onCommit((file, owner) => {
        const scene = getTraceScene();
        if (!scene || scene.getDocumentRoot() !== owner) return;
        saveSessionToScene(scene, file).catch((err: unknown) => console.warn('[AI] Could not save the conversation into the scene:', err));
    });

    // Initial render (hidden — shows FAB only)
    render();

    return {
        toggle: () => {
            visible = !visible;
            render();
        },
        isVisible: () => visible,
        dispose: () => {
            window.removeEventListener('trace-viewer:selection-changed', onSelectionChanged);
            window.removeEventListener('trace-viewer:scene-changed', onSceneChanged);
            unsubSession();
            agentService.setSceneFileImporter(null);
            root?.unmount();
            root = null;
            container.remove();
        },
    };
}
